import { useEffect, useState } from 'react'
import {
  Alert,
  DSInputType,
  EmptyContent,
  HeadItemType,
  Icon,
  Input,
  LoadingHavaianas,
  Page,
  Sidebar,
  SlimButton,
  Table,
  ToggleButton,
  Tooltip,
  Typography
} from 'components'
import { useDispatch, useSelector } from 'store/store'
import { useForm, Controller } from 'react-hook-form'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import TrackingModal from './components/TrackingModal'
import {
  dispatchRangeReview,
  dispatchRangeUpdate,
  dispatchRangeUpdatePatch,
  dispatchRangeVisualization,
  dispatchSendRange,
  getTeamRanges
} from 'store/BackofficeContext/backofficeSlice'
import { toast } from 'react-toastify'
import { theme } from 'styles/theme/hav-theme'
import { TeamRange } from 'services/backoffice/interfaces'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { officeByMarkets, Offices } from 'constants/enums'
import { isObjectEmpty } from 'commons/utils/object'

import * as S from './styles'

type EditableValues = {
  [x: string]: { maxPercentage: number; maxAmount: number }
}

const BackofficeRanges = () => {
  const dispatch = useDispatch()
  const { verifyPermission } = usePermissions()
  const { offices: userOffices } = useSelector((state) => state.auth)
  const { teamRanges, rangeVersion, loadingData } = useSelector(
    (state) => state.backoffice
  )
  const { control, setValue, handleSubmit } = useForm<EditableValues>()
  const [selectedOffice, setSelectedOffice] = useState(
    userOffices.length ? userOffices[0] : Offices.AC60
  )
  const [isEditing, setIsEditing] = useState(false)
  const [approveModal, setApproveModal] = useState(false)
  const [rejectModal, setRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [openTracking, setOpenTracking] = useState(false)

  const canEdit = verifyPermission(SCREENS.BACKOFFICE_RANGE, ACTIONS_SCREENS.SAVE)
  const canApprove = verifyPermission(SCREENS.BACKOFFICE_RANGE, ACTIONS_SCREENS.APPROVE)
  const canReject = verifyPermission(SCREENS.BACKOFFICE_RANGE, ACTIONS_SCREENS.DISMISS)
  const canReview = (canApprove || canReject) && userOffices.includes(selectedOffice)

  const rangeStatus = {
    approved: 100,
    pending: 20,
    analysis: 30,
    rejected: 90
  }

  const handleEdit = () => {
    setIsEditing(true)
    setPendingFields()
  }

  // NOTE: Indiciador de última atualização ocultado temporariamente
  // const latestUpadateDate = useMemo(() => {
  //   const dates = teamRanges.map((range) => new Date(range.DataAlteracao))
  //   const maxDate = getMaxDate(dates)

  //   if (!maxDate) return ''

  //   return formatDate(maxDate, 'DD/MM')
  // }, [teamRanges])

  const getStatusColor = (status: number) => {
    if (status === rangeStatus.approved) return theme.colors.alert.success[40]

    if ([rangeStatus.pending, rangeStatus.analysis].includes(status)) {
      return theme.colors.alert.warning[40]
    }

    if (status === rangeStatus.rejected) return theme.colors.alert.danger[40]

    return theme.colors.neutral.high
  }

  const getPercentValue = (range: TeamRange) => {
    const changedTeams = rangeVersion.Regionais.map((team) => team.Codigo)
    if (changedTeams.includes(range.CodigoEquipe) && !rangeVersion.Visualizacao) {
      const teamVersion = rangeVersion.Regionais.find(
        (team) => team.Codigo === range.CodigoEquipe
      )

      if (
        teamVersion &&
        (canEdit || rangeVersion.Status.Codigo !== rangeStatus.pending)
      ) {
        const prevVal = teamVersion.PorcentagemAnterior
        const propVal = teamVersion.Porcentagem

        if (prevVal !== propVal) {
          return (
            <S.ColoredText color={getStatusColor(rangeVersion.Status.Codigo)}>
              {`${numberToDecimalString(prevVal)}% -> ${numberToDecimalString(propVal)}%`}
            </S.ColoredText>
          )
        }
      }
    }

    return `${numberToDecimalString(range.PorcentagemMaxima)}%`
  }

  const getCurrencyValue = (range: TeamRange) => {
    const changedTeams = rangeVersion.Regionais.map((team) => team.Codigo)

    if (changedTeams.includes(range.CodigoEquipe) && !rangeVersion.Visualizacao) {
      const teamVersion = rangeVersion.Regionais.find(
        (team) => team.Codigo === range.CodigoEquipe
      )

      if (
        teamVersion &&
        (canEdit || rangeVersion.Status.Codigo !== rangeStatus.pending)
      ) {
        const prevVal = teamVersion.ValorAnterior
        const propVal = teamVersion.Valor

        if (prevVal !== propVal) {
          return (
            <S.ColoredText color={getStatusColor(rangeVersion.Status.Codigo)}>
              {`R$${numberToDecimalString(prevVal)} ->
              R$${numberToDecimalString(propVal)}`}
            </S.ColoredText>
          )
        }
      }
    }

    return `${formatToPTBRNumber(range.ValorMaximo)}`
  }

  const tableHead: HeadItemType[] = [
    { label: 'Regional', columnName: 'Regional' },
    { label: 'Nome regional', columnName: 'NomeRegional' },
    { label: 'Responsável', columnName: 'Responsavel' },
    { label: '% máx. Alçada', columnName: 'PorcentagemMaxAlcada' },
    { label: 'R$ máx. Alçada', columnName: 'ValorMaxAlcada' }
  ]

  const validatePercentage = (value: string) => {
    const regex = /^\d{1,2}((,|\.)\d{0,2})?$|^100$/

    return regex.test(value) || value === ''
  }

  const validateCurrency = (value: string) => {
    const regex = /^\d{1,6}((,|\.)\d{0,2})?$/

    return regex.test(value) || value === ''
  }

  const tableBody = () =>
    teamRanges.map((item) => {
      const percentField = () => {
        if (isEditing) {
          return (
            <Controller
              name={`[${item.CodigoEquipe}].maxPercentage`}
              control={control}
              render={({ field }) => (
                <Input
                  addorment
                  design={DSInputType.PERCENT_INPUT}
                  width={120}
                  height={28}
                  value={field.value}
                  onChange={(e) => {
                    const value = e.target.value
                    if (validatePercentage(value)) field.onChange(value)
                  }}
                />
              )}
            />
          )
        }

        return getPercentValue(item)
      }

      const amountField = () => {
        if (isEditing) {
          return (
            <Controller
              name={`[${item.CodigoEquipe}].maxAmount`}
              control={control}
              render={({ field }) => (
                <Input
                  addorment
                  design={DSInputType.CURRENCY_INPUT}
                  width={120}
                  height={28}
                  value={field.value}
                  onChange={(e) => {
                    const value = e.target.value
                    if (validateCurrency(value)) field.onChange(value)
                  }}
                  maxLength={6}
                />
              )}
            />
          )
        }

        return getCurrencyValue(item)
      }

      return {
        id: item.CodigoEquipe,
        content: [
          item.CodigoEquipe,
          item.NomeEquipe ?? '-',
          item.ResponsavelEquipe,
          percentField(),
          amountField()
        ]
      }
    })

  const resetFields = () => {
    teamRanges.forEach((item) => {
      setValue(item.CodigoEquipe, {
        maxPercentage: item.PorcentagemMaxima,
        maxAmount: item.ValorMaximo
      })
    })
  }

  const setPendingFields = () => {
    if (rangeVersion.Regionais.length > 0 && !rangeVersion.Visualizacao) {
      rangeVersion.Regionais.forEach((item) => {
        setValue(item.Codigo, {
          maxPercentage: item.Porcentagem,
          maxAmount: item.Valor
        })
      })
    }
  }

  const handleCancelEdit = () => {
    resetFields()
    setIsEditing(false)
  }

  const onSubmit = (data: EditableValues) => {
    setIsEditing(false)
    const teams = Object.keys(data).map((key) => {
      return {
        codigoEquipe: key,
        valorDescontoNovo: data[key].maxAmount,
        percentualDescontoNovo: data[key].maxPercentage
      }
    })

    const dirtyTeams = teams.filter(
      (team) =>
        typeof team.valorDescontoNovo === 'string' ||
        typeof team.percentualDescontoNovo === 'string'
    )

    const emptyValues = dirtyTeams.filter(
      (team) =>
        `${team.valorDescontoNovo}` === '' || `${team.percentualDescontoNovo}` === ''
    )
    if (emptyValues.length > 0) {
      resetFields()
      toast.error(
        'Não foi possível salvar as suas alterações devido à presença de campos vazios.'
      )
      return
    }

    const formattedTeams = dirtyTeams.map((team) => {
      return {
        codigoEquipe: team.codigoEquipe,
        valorDescontoNovo: Number(`${team.valorDescontoNovo}`.replace(',', '.')),
        percentualDescontoNovo: Number(`${team.percentualDescontoNovo}`.replace(',', '.'))
      }
    })

    if (rangeVersion.Status.Codigo === rangeStatus.pending) {
      const payload = {
        codigoVersao: rangeVersion.CodigoVersao,
        alcadas: formattedTeams
      }

      dispatch(dispatchRangeUpdatePatch(payload, selectedOffice))
    } else {
      const hasChanges = formattedTeams.some((team) => {
        const teamVersion = teamRanges.find(
          (range) => range.CodigoEquipe === team.codigoEquipe
        )

        return (
          teamVersion &&
          (teamVersion.ValorMaximo !== team.valorDescontoNovo ||
            teamVersion.PorcentagemMaxima !== team.percentualDescontoNovo)
        )
      })

      if (!hasChanges) {
        resetFields()
        return
      }

      const payload = {
        escritorio: selectedOffice,
        alcadas: formattedTeams
      }

      dispatch(dispatchRangeUpdate(payload, selectedOffice))
    }
  }

  const handleVisualization = () => {
    dispatch(dispatchRangeVisualization(rangeVersion.CodigoVersao, selectedOffice))
  }

  const handleSend = () => {
    dispatch(dispatchSendRange(rangeVersion.CodigoVersao, selectedOffice))
  }

  const handleReview = (approval: boolean) => {
    const payload = {
      codigoVersao: rangeVersion.CodigoVersao,
      observacao: !approval ? rejectReason : '',
      aprovado: approval
    }

    dispatch(dispatchRangeReview(payload, selectedOffice))
    setRejectReason('')
  }

  useEffect(() => {
    resetFields()
  }, [teamRanges])

  useEffect(() => {
    dispatch(getTeamRanges(selectedOffice))
  }, [selectedOffice])

  const renderToggleButtons = () => {
    const officesMI = officeByMarkets.MI

    return officesMI.map((office) => (
      <ToggleButton
        key={office}
        content={`Escritório ${office}`}
        active={selectedOffice === office}
        onClick={() => setSelectedOffice(office)}
        disabled={loadingData || isEditing}
      />
    ))
  }

  return (
    <Page fullwidth>
      <S.AlertWrapper>
        <S.Wrapper>
          <Sidebar>
            <Sidebar.Divider />
            <Sidebar.Item label="Alçadas" icon={<Icon icon="trending-up" />} active />
            <Sidebar.Divider />
          </Sidebar>

          <S.Content>
            {!loadingData && !isObjectEmpty(rangeVersion) && (
              <>
                {canEdit && rangeVersion.Status.Codigo === rangeStatus.pending && (
                  <Alert
                    kind="info"
                    title="Alterações realizadas!"
                    message="Após realizar todas as  alterações necessárias, não esqueça de solicitar aprovação."
                    actionLabel="Solicitar aprovação"
                    actionIcon={<Icon icon="send" size="15" />}
                    handleAction={handleSend}
                    fullWidth
                  />
                )}
              </>
            )}
            <S.Header>
              <S.TitleRow>
                <S.IconTitleRow>
                  <Icon icon="trending-up" color={theme.colors.primary.main} size="30" />
                  <S.Title>Configuração de Política de Alçadas</S.Title>
                </S.IconTitleRow>
                <S.ColDivider />
                <S.Title>Aprovação de negociação diferenciada</S.Title>
              </S.TitleRow>
              <SlimButton
                variant="ghost"
                content="Tracking"
                icon={<Icon icon="track" size="15" />}
                onClick={() => setOpenTracking(true)}
              />
            </S.Header>
            <S.Card>
              <Typography design="hav-h7-text">
                {canEdit ? 'Defina' : 'Confira abaixo'} o percentual e limite de valor
                para cada Regional:
              </Typography>
              <S.ButtonsRow>{renderToggleButtons()}</S.ButtonsRow>
              <S.OfficeTitle>
                <Typography design="hav-h5-text">Escritório {selectedOffice}</Typography>
                {/* NOTE: Indiciador de última atualização ocultado temporariamente*/}
                {/* <S.OfficeTooltip>
                  {loadingData ? (
                    <Skeleton width={165} height={17} />
                  ) : (
                    <Typography design="hav-h8-text" className="office-info">
                      Política atualizada em {latestUpadateDate}
                    </Typography>
                  )}
                </S.OfficeTooltip> */}
              </S.OfficeTitle>
              {!loadingData && !isObjectEmpty(rangeVersion) && (
                <>
                  {canEdit &&
                    !rangeVersion.Visualizacao &&
                    rangeVersion.Status.Codigo === rangeStatus.approved && (
                      <Alert
                        kind="success"
                        title="Atualização de política de alçadas aprovada e vigente."
                        actionLabel="Ok"
                        handleAction={handleVisualization}
                        fullWidth
                      />
                    )}
                  {canEdit &&
                    !rangeVersion.Visualizacao &&
                    rangeVersion.Status.Codigo === rangeStatus.rejected && (
                      <Alert
                        kind="error"
                        title="Atualização de política de alçadas reprovada."
                        message={`Justificativa: ${rangeVersion.ObservacaoAnalise}`}
                        actionLabel="Ok"
                        handleAction={handleVisualization}
                        fullWidth
                      />
                    )}
                  {canEdit && rangeVersion.Status.Codigo === rangeStatus.analysis && (
                    <Alert
                      kind="warning"
                      title=" Alterações pendentes de aprovação da diretoria comercial."
                      fullWidth
                    />
                  )}
                  {canReview && rangeVersion.Status.Codigo === rangeStatus.analysis && (
                    <Alert
                      kind="warning"
                      title=" Alterações pendentes de aprovação."
                      fullWidth
                    />
                  )}
                </>
              )}

              {loadingData ? (
                <LoadingHavaianas />
              ) : !teamRanges.length ? (
                <EmptyContent
                  title="Configuração de alçadas indisponível"
                  description="Não foi possível encontrar a configuração de política de alçadas do escritório selecionado."
                />
              ) : (
                <Table headData={tableHead} bodyData={tableBody()} rowHeight={50} />
              )}
              {!loadingData && !!teamRanges.length && (
                <S.TableActions>
                  <S.RowDivider />
                  {canEdit && (
                    <S.ActionButtons>
                      {isEditing ? (
                        <>
                          <SlimButton
                            variant="secondary"
                            content="Cancelar edição"
                            icon={<Icon icon="close" size="15" />}
                            onClick={handleCancelEdit}
                          />
                          <SlimButton
                            variant="primary"
                            content="Salvar"
                            icon={<Icon icon="save" size="15" />}
                            onClick={handleSubmit(onSubmit)}
                          />
                        </>
                      ) : (
                        <Tooltip
                          title={
                            'Não é possível editar enquanto estiver no aguardo de aprovação'
                          }
                          arrow
                          placement="top"
                          disableHoverListener={
                            rangeVersion.Status.Codigo !== rangeStatus.analysis
                          }
                        >
                          <div>
                            <SlimButton
                              variant="primary"
                              content="Editar"
                              icon={<Icon icon="edit" size="15" />}
                              onClick={handleEdit}
                              disabled={
                                rangeVersion.Status.Codigo === rangeStatus.analysis
                              }
                            />
                          </div>
                        </Tooltip>
                      )}
                    </S.ActionButtons>
                  )}
                  {canReview && rangeVersion.Status.Codigo === rangeStatus.analysis && (
                    <S.ActionButtons>
                      <S.RejectButton
                        onClick={() => setRejectModal(true)}
                        disabled={!canReject}
                      >
                        <Icon
                          icon="cancel"
                          color={theme.colors.neutral.white}
                          size="16"
                        />
                        Reprovar
                      </S.RejectButton>
                      <S.ApproveButton
                        onClick={() => setApproveModal(true)}
                        disabled={!canApprove}
                      >
                        <Icon
                          icon="task-alt"
                          color={theme.colors.neutral.white}
                          size="16"
                        />
                        Aprovar
                      </S.ApproveButton>
                    </S.ActionButtons>
                  )}
                </S.TableActions>
              )}
            </S.Card>
          </S.Content>
        </S.Wrapper>
      </S.AlertWrapper>
      {approveModal && (
        <ModalContentLayout
          title="Aprovar atualização"
          titleSize={22}
          width={390}
          description="Tem certeza que deseja aprovar a atualização de política de alçadas?"
          closeAction={() => setApproveModal(false)}
        >
          <S.ModalActions>
            <SlimButton
              variant="secondary"
              content="Cancelar"
              onClick={() => setApproveModal(false)}
            />
            <SlimButton
              variant="primary"
              content="Aprovar"
              onClick={() => {
                setApproveModal(false)
                handleReview(true)
              }}
            />
          </S.ModalActions>
        </ModalContentLayout>
      )}
      {rejectModal && (
        <ModalContentLayout
          title="Reprovar atualização"
          titleSize={22}
          width={390}
          description="Insira uma justificativa para a reprovação."
          closeAction={() => {
            setRejectReason('')
            setRejectModal(false)
          }}
        >
          <div>
            <Input
              label="Descrição"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Descreva aqui a justificativa"
              required
              rows={3}
              height={90}
              maxLength={255}
              showMaxLength
            />
          </div>
          <S.ModalActions>
            <SlimButton
              variant="secondary"
              content="Cancelar"
              onClick={() => {
                setRejectReason('')
                setRejectModal(false)
              }}
            />
            <SlimButton
              variant="danger"
              content="Reprovar"
              disabled={!rejectReason}
              onClick={() => {
                setRejectModal(false)
                handleReview(false)
              }}
            />
          </S.ModalActions>
        </ModalContentLayout>
      )}
      {openTracking && <TrackingModal setOpen={setOpenTracking} />}
    </Page>
  )
}

export default BackofficeRanges
